import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { distinctUntilChanged, Observable } from 'rxjs';
import { NetzbetreiberService } from '../../service/netzbetreiber/netzbetreiber.service';
import { debounceTime, map, mergeMap, startWith, takeWhile } from 'rxjs/operators';
import { NetzbetreiberFilter } from '../../service/netzbetreiber/netzbetreiber-filter';
import { DienstleistungsartService } from '../../service/dienstleistungsmodule/dienstleistungsart.service';
import { DienstleistungsmoduleService } from '../../service/dienstleistungsmodule/dienstleistungsmodule.service';
import { NetzbetreiberExportService } from '../../service/netzbetreiber/netzbetreiber-export.service';

const FILTER_DEBOUNCE_TIME = 250;
const FILTER_OPTION_LIMIT = 50;

@Component({
    selector: 'redispatch-netzbetreiber-filter',
    templateUrl: './netzbetreiber-filter.component.html',
    styleUrls: [
        './netzbetreiber-filter.component.scss',
    ],
    standalone: false,
})
export class NetzbetreiberFilterComponent implements OnInit, OnDestroy {

    stadtwerkeId?: string;

    @Input() initialFilter?: NetzbetreiberFilter;
    @Output() filter = new EventEmitter<NetzbetreiberFilter>();

    filterFormGroup: UntypedFormGroup;

    filteredName$: Observable<string[]>;
    filteredStadtwerkeId$: Observable<string[]>;
    filteredTeilgebiete$: Observable<string[]>;
    filteredNetzbetreibernummer$: Observable<string[]>;
    filteredGeschaeftspartnerId$: Observable<string[]>;
    filteredDienstleistungsart$: Observable<string[]>;
    allDienstleistungsmodul$: Observable<string[]>;

    private componentDestroyed = false;

    private netzbetreiberService = inject(NetzbetreiberService);
    private netzbetreiberExportService = inject(NetzbetreiberExportService);
    private dienstleistungsartService = inject(DienstleistungsartService);
    private dienstleistungsmoduleService = inject(DienstleistungsmoduleService);

    private formBuilder = inject(UntypedFormBuilder);

    async ngOnInit() {
        this.filterFormGroup = this.formBuilder.group({
            name: [
                this.initialFilter.name,
            ],
            stwId: [
                this.initialFilter.stwId,
            ],
            teilgebiet: [
                this.initialFilter.teilgebiet,
            ],
            netzbetreibernummer: [
                this.initialFilter.netzbetreibernummer,
            ],
            geschaeftspartnerId: [
                this.initialFilter.geschaeftspartnerId,
            ],
            lastImportDate: this.formBuilder.group({
                start: [
                    this.initialFilter.lastImportDate?.start,
                ],
                end: [
                    this.initialFilter.lastImportDate?.end,
                ],
            }),
            dienstleistungsart: [
                this.initialFilter.dienstleistungsart,
            ],
            dienstleistungsmoduleBeschreibung: [
                this.initialFilter.dienstleistungsmoduleBeschreibung,
            ],
        });

        this.filterFormGroup.valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.value),
                takeWhile(() => !this.componentDestroyed),
                map(val => {
                    return new NetzbetreiberFilter(
                        val.name,
                        val.stwId,
                        val.teilgebiet,
                        val.netzbetreibernummer,
                        val.geschaeftspartnerId,
                        val.lastImportDate,
                        val.dienstleistungsart,
                        val.dienstleistungsmoduleBeschreibung,
                    );
                }),
            )
            .subscribe(val => this.filter.next(val));

        this.filteredName$ = this.filterFormGroup.get('name').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('name').value),
                distinctUntilChanged(),
                mergeMap(netzbetreiberName => this.netzbetreiberService.getAlleNetzbetreiberNamen(netzbetreiberName)),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredStadtwerkeId$ = this.filterFormGroup.get('stwId').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('stwId').value),
                distinctUntilChanged(),
                mergeMap(stadtwerkeId => this.netzbetreiberService.getAlleNetzbetreiberIds(stadtwerkeId)),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredTeilgebiete$ = this.filterFormGroup.get('teilgebiet').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('teilgebiet').value),
                distinctUntilChanged(),
                mergeMap(teilgebiet => this.netzbetreiberService.getAlleTeilgebiete(teilgebiet)),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredNetzbetreibernummer$ = this.filterFormGroup.get('netzbetreibernummer').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('netzbetreibernummer').value),
                distinctUntilChanged(),
                mergeMap(netzbetreibernummer => this.netzbetreiberService.getAlleNetzbetreiberNummern(netzbetreibernummer)),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredGeschaeftspartnerId$ = this.filterFormGroup.get('geschaeftspartnerId').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('geschaeftspartnerId').value),
                distinctUntilChanged(),
                mergeMap(geschaeftspartnerId => this.netzbetreiberService.getAlleGeschaeftspartnerIds(geschaeftspartnerId)),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredDienstleistungsart$ = this.filterFormGroup.get('dienstleistungsart').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('dienstleistungsart').value),
                distinctUntilChanged(),
                mergeMap(dienstleistungsart => this.dienstleistungsartService.getAlleDienstleistungsartenNamen(dienstleistungsart)),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.allDienstleistungsmodul$ = this.dienstleistungsmoduleService.listDienstleistungsModuleBeschreibung().pipe(
            map((module) => module.map(value => value.title)),
        );
    }

    ngOnDestroy(): void {
        this.componentDestroyed = true;
    }

    public exportNetzbetreiber() {
        this.netzbetreiberExportService.exportNetzbetreiber();
    }
}
